import { useEffect } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import LoadingIndicator from '../../common/LoadingIndicator'
import useFetch from '../../hooks/useFetch'
import { urlApi } from '../../config'
import { CurrencyChip, currencySymbol } from './RateTable'
import { formatDecimal, formatPercent } from './utils'

const MUTED = 'text-gray-500 dark:text-gray-400'

const formatValue = (field, value) => {
  const number = formatDecimal(value) || '—'
  return field.kind === 'price' ? `${currencySymbol(field.group.currency)} ${number}` : `${number} ×`
}

export default function ConfirmChangesDialog({
  open,
  changes,
  payload,
  saving,
  saveError,
  onCancel,
  onConfirm,
  onSaved,
  onSaveError
}) {
  const hasBigChange = changes.some(change => change.state.bigChange)

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Confirmar cambios de precios</DialogTitle>
      <DialogContent>
        <p className={`text-sm ${MUTED}`}>
          Se aplican a los precios sugeridos en producción y pueden tardar hasta 5 minutos en
          reflejarse.
        </p>

        <ul className='mt-4 rounded-lg ring-1 ring-gray-200 dark:ring-dk-border divide-y divide-gray-100 dark:divide-dk-border'>
          {changes.map(({ field, state }) => (
            <li key={field.id} className='flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3'>
              <div className='min-w-0 flex-1'>
                <p className='flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100'>
                  {field.group.label} · {field.scope}
                  <CurrencyChip currency={field.group.currency} />
                </p>
                <p className={`text-xs mt-0.5 ${MUTED}`}>
                  {field.kind === 'price' ? 'Precio por km' : 'Multiplicador máx.'}
                </p>
              </div>
              <div className='flex items-center gap-2 text-sm tabular-nums'>
                <span className={`${MUTED} line-through decoration-1`}>
                  {formatValue(field, state.previous)}
                </span>
                <FontAwesomeIcon icon={faArrowRight} className={`text-xs ${MUTED}`} aria-label='pasa a' />
                <span className='font-semibold text-gray-800 dark:text-gray-100'>
                  {formatValue(field, state.value)}
                </span>
                {state.ratio != null && (
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                      state.bigChange
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-dk-raised dark:text-gray-300'
                    }`}
                  >
                    {formatPercent(state.ratio)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {hasBigChange && (
          <p className='flex items-start gap-2 mt-4 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2'>
            <FontAwesomeIcon icon={faTriangleExclamation} className='mt-0.5' aria-hidden />
            Hay cambios de más del 25%. Revisá que los valores sean correctos antes de guardar.
          </p>
        )}

        {saveError && (
          <p role='alert' className='mt-4 text-sm text-red-600 dark:text-red-400'>
            No se pudieron guardar los cambios. Probá de nuevo.
          </p>
        )}

        {saving && <SaveRequest payload={payload} onSuccess={onSaved} onError={onSaveError} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button
          className='px-3 py-2 rounded-lg bg-light_gray dark:bg-dk-raised text-black dark:text-gray-100 hover:opacity-90 disabled:opacity-50 transition-opacity'
          onClick={onCancel}
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-60 transition-opacity'
          onClick={onConfirm}
          disabled={saving}
        >
          {saving && <LoadingIndicator color='#ffffff' size={12} />}
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </DialogActions>
    </Dialog>
  )
}

const SaveRequest = ({ payload, onSuccess, onError }) => {
  const { loading, responseOk } = useFetch(`${urlApi}/admin/pricing`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })

  useEffect(() => {
    if (loading) return
    if (responseOk) onSuccess()
    else onError()
  }, [loading, responseOk])

  return null
}
